/*
 * ZelSnake — classic snake for ZelTechnology members.
 * Pure HTML5 canvas + vanilla JS, site colors, keyboard + swipe support.
 */

export const GAME_NAME = "snake";

const TILE = 20;
const COLS = 24;
const ROWS = 20;
const WIDTH = COLS * TILE;
const HEIGHT = ROWS * TILE;
const BASE_INTERVAL = 11; // frames per move at start (60fps -> ~5.5 moves/s)

const COLORS = {
  bg: "#0f172a",
  grid: "#1e293b",
  snake: "#2563eb",
  snakeHead: "#12bfbe",
  food: "#ec4899",
  text: "#e5e7eb",
};

export function createGame(canvas, { onScore = () => {}, onGameOver = () => {} } = {}) {
  const ctx = canvas.getContext("2d");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;

  let snake, dir, nextDir, food, score, running, raf, frame, interval;
  let touchStart = null;

  function spawnFood() {
    let x, y, onSnake;
    do {
      x = Math.floor(Math.random() * COLS);
      y = Math.floor(Math.random() * ROWS);
      onSnake = snake.some((s) => s.x === x && s.y === y);
    } while (onSnake);
    food = { x, y };
  }

  function resetState() {
    snake = [{ x: 6, y: 10 }, { x: 5, y: 10 }, { x: 4, y: 10 }];
    dir = { x: 1, y: 0 };
    nextDir = null;
    score = 0;
    interval = BASE_INTERVAL;
    frame = 0;
    running = true;
    spawnFood();
    onScore(score);
  }

  function setDir(name) {
    const d = { up: { x: 0, y: -1 }, down: { x: 0, y: 1 }, left: { x: -1, y: 0 }, right: { x: 1, y: 0 } }[name];
    if (!d) return;
    // prevent reversing into yourself
    if (d.x === -dir.x && d.y === -dir.y) return;
    nextDir = d;
  }

  function step() {
    if (nextDir) { dir = nextDir; nextDir = null; }

    const head = { x: snake[0].x + dir.x, y: snake[0].y + dir.y };

    // wall or self collision -> game over
    const hitWall = head.x < 0 || head.x >= COLS || head.y < 0 || head.y >= ROWS;
    const hitSelf = snake.some((s) => s.x === head.x && s.y === head.y);
    if (hitWall || hitSelf) {
      running = false;
      onGameOver(score);
      return;
    }

    snake.unshift(head);

    if (head.x === food.x && head.y === food.y) {
      score += 10;
      onScore(score);
      spawnFood();
      // speed up slightly every food
      if (interval > 4) interval -= 0.25;
    } else {
      snake.pop();
    }
  }

  function update() {
    if (!running) return;
    frame++;
    if (frame >= interval) {
      frame = 0;
      step();
    }
  }

  function draw() {
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    // subtle grid
    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth = 1;
    for (let x = 0; x <= COLS; x++) {
      ctx.beginPath(); ctx.moveTo(x * TILE, 0); ctx.lineTo(x * TILE, HEIGHT); ctx.stroke();
    }
    for (let y = 0; y <= ROWS; y++) {
      ctx.beginPath(); ctx.moveTo(0, y * TILE); ctx.lineTo(WIDTH, y * TILE); ctx.stroke();
    }

    // food
    ctx.fillStyle = COLORS.food;
    ctx.beginPath();
    ctx.arc(food.x * TILE + TILE / 2, food.y * TILE + TILE / 2, TILE / 2 - 3, 0, Math.PI * 2);
    ctx.fill();

    // snake
    snake.forEach((s, i) => {
      ctx.fillStyle = i === 0 ? COLORS.snakeHead : COLORS.snake;
      const pad = i === 0 ? 1 : 2;
      ctx.fillRect(s.x * TILE + pad, s.y * TILE + pad, TILE - pad * 2, TILE - pad * 2);
    });

    // HUD
    ctx.fillStyle = COLORS.text;
    ctx.font = "12px monospace";
    ctx.fillText(`SCORE ${score}`, 6, 14);
    if (!running) {
      ctx.fillStyle = "rgba(15, 23, 42, 0.88)";
      ctx.fillRect(0, HEIGHT / 2 - 34, WIDTH, 48);
      ctx.fillStyle = "#ef4444";
      ctx.font = "bold 18px monospace";
      ctx.fillText("GAME OVER  —  press R to play again", 18, HEIGHT / 2 - 8);
    }
  }

  function loop() {
    update();
    draw();
    raf = requestAnimationFrame(loop);
  }

  const keyHandler = (e) => {
    const map = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right", w: "up", s: "down", a: "left", d: "right" };
    if (map[e.key]) {
      e.preventDefault();
      setDir(map[e.key]);
    }
    if (e.key === "r" || e.key === "R") start();
  };

  const touchStartHandler = (e) => {
    touchStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const touchEndHandler = (e) => {
    if (!touchStart) return;
    const dx = e.changedTouches[0].clientX - touchStart.x;
    const dy = e.changedTouches[0].clientY - touchStart.y;
    if (Math.abs(dx) > Math.abs(dy)) {
      setDir(dx > 0 ? "right" : "left");
    } else {
      setDir(dy > 0 ? "down" : "up");
    }
    touchStart = null;
  };

  window.addEventListener("keydown", keyHandler);
  canvas.addEventListener("touchstart", touchStartHandler, { passive: true });
  canvas.addEventListener("touchend", touchEndHandler, { passive: true });

  function start() {
    if (raf) cancelAnimationFrame(raf);
    resetState();
    raf = requestAnimationFrame(loop);
  }

  start();

  return {
    setDirection(name) {
      setDir(name);
    },
    restart: start,
    destroy() {
      window.removeEventListener("keydown", keyHandler);
      canvas.removeEventListener("touchstart", touchStartHandler);
      canvas.removeEventListener("touchend", touchEndHandler);
      if (raf) cancelAnimationFrame(raf);
    },
  };
}
